export interface TextAnimatorOptions {
  wordsPerSecond?: number
  onUpdate?: (visibleText: string) => void
  onComplete?: () => void
  /** Length of the revealed text that is safe to show; the rest waits for more words */
  getStableLength?: (visibleText: string) => number
}

export interface TextAnimator {
  animate(newText: string): void
  cancel(): void
  getVisibleText(): string
  isAnimating(): boolean
  reset(): void
}

function tokenizeText(text: string): string[] {
  if (!text) return []

  // If text is only whitespace, return it as a single token.
  if (/^\s+$/.test(text)) {
    return [text]
  }

  // Preserve leading whitespace/newlines as an explicit first token so that
  // headings and tables are not immediately touching prior content.
  const leadingWhitespaceMatch = text.match(/^\s+/)
  const leadingWhitespace = leadingWhitespaceMatch?.[0] ?? ''
  const remainingText = leadingWhitespace ? text.slice(leadingWhitespace.length) : text

  const tokens: string[] = []
  if (leadingWhitespace) {
    tokens.push(leadingWhitespace)
  }

  const regex = /\S+\s*/g
  let match: RegExpExecArray | null

  while ((match = regex.exec(remainingText)) !== null) {
    tokens.push(match[0])
  }

  return tokens
}

export function createTextAnimator(options: TextAnimatorOptions = {}): TextAnimator {
  const { wordsPerSecond = 20, onUpdate, onComplete, getStableLength } = options

  const delayMs = 1000 / wordsPerSecond

  let allTokens: string[] = []
  let currentVisibleIndex = 0
  let intervalId: ReturnType<typeof setInterval> | null = null
  let animationQueue: string[] = []
  let emittedLength = 0

  function getVisibleText(): string {
    return allTokens.slice(0, currentVisibleIndex).join('')
  }

  function clearAnimation(): void {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  function revealNextWord(): void {
    if (animationQueue.length === 0) {
      clearAnimation()
      if (onComplete) {
        onComplete()
      }
      return
    }

    const nextToken = animationQueue.shift()!
    allTokens.push(nextToken)
    currentVisibleIndex += 1

    const visibleText = getVisibleText()
    const stableLength = getStableLength ? getStableLength(visibleText) : visibleText.length
    // Never shrink what is already on screen; wait until the stable part grows.
    if (stableLength <= emittedLength) return

    emittedLength = stableLength
    if (onUpdate) {
      onUpdate(visibleText.slice(0, stableLength))
    }
  }

  return {
    animate(newText: string): void {
      const newTokens = tokenizeText(newText)

      if (newTokens.length === 0) return

      animationQueue.push(...newTokens)

      if (intervalId === null) {
        intervalId = setInterval(revealNextWord, delayMs)
        revealNextWord()
      }
    },

    cancel(): void {
      clearAnimation()

      while (animationQueue.length > 0) {
        const token = animationQueue.shift()!
        allTokens.push(token)
        currentVisibleIndex += 1
      }

      const visibleText = getVisibleText()
      emittedLength = visibleText.length
      if (onUpdate) {
        onUpdate(visibleText)
      }
    },

    getVisibleText(): string {
      return getVisibleText()
    },

    isAnimating(): boolean {
      return intervalId !== null || animationQueue.length > 0
    },

    reset(): void {
      clearAnimation()
      allTokens = []
      currentVisibleIndex = 0
      animationQueue = []
      emittedLength = 0
    },
  }
}

